import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/PaymentSandF.css";
import { PENDING_PAYMENT_KEY } from "./Checkout";

const PaymentFailed = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const pgStatus = params.get("status");
  const txn = params.get("txn");
  let pending = null;
  try {
    pending = JSON.parse(localStorage.getItem(PENDING_PAYMENT_KEY) || "null");
  } catch (e) {
    pending = null;
  }
  const { error } = location.state || {};
  const book = location.state?.book || (pending && (!txn || pending.txn === txn) ? pending.book : null);
  const STATUS_MESSAGES = {
    CUSTOMER_CANCELLED: "You cancelled the payment.",
    AUTHENTICATION_TIMEOUT: "The payment page timed out. Please try again.",
    GENERAL_DECLINE: "The payment was declined. Please try another card or method.",
    CONDITIONAL_DECLINE: "The payment was declined by the bank. Please try again later.",
    SYSTEM_ERROR: "The payment gateway had a problem. Please try again in a few minutes.",
  };
  const result = { message: STATUS_MESSAGES[pgStatus], error_code: pgStatus, transaction_id: txn };
  const [showErrorAnimation, setShowErrorAnimation] = useState(true);

  useEffect(() => {
    // Show error animation for 2 seconds
    const timer = setTimeout(() => {
      setShowErrorAnimation(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleRetryPayment = () => {
    if (book) navigate("/checkout", { state: { book } });
    else navigate("/allpages");
  };

  const handleGoToLibrary = () => {
    navigate("/allpages");
  };

  const handleContactSupport = () => {
    // You can implement support contact functionality here
    window.location.href = "mailto:info@isfinformatica.com";
  };

  const formatTransactionId = (id) => {
    if (!id) return "N/A";
    return id.length > 12 ? `${id.substring(0, 12)}...` : id;
  };

  const getErrorMessage = () => {
    if (result?.message) return result.message;
    if (error) return error;
    return "Your payment could not be processed at this time.";
  };

  const getErrorCode = () => {
    if (result?.error_code) return result.error_code;
    return "PAYMENT_FAILED";
  };

  return (
    <div className="payment-result-container failed">
      {showErrorAnimation && (
        <div className="error-particles">
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
        </div>
      )}
      
      <div className="result-card">
        {/* Error Animation */}
        <div className="error-animation">
          <div className="error-circle">
            <svg className="error-x" width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path 
                d="M18 6L6 18" 
                stroke="white" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <path 
                d="M6 6L18 18" 
                stroke="white" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* Error Content */}
        <div className="result-content">
          <h1>Payment Failed</h1>
          <p className="success-message">{getErrorMessage()}</p>

          {/* Transaction Details */}
          <div className="transaction-details">
            {txn && (
              <div className="detail-row">
                <span className="label">Transaction ID</span>
                <span className="value transaction-id">{formatTransactionId(txn)}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="label">Book</span>
              <span className="value">{book?.pdf_name || "Unknown Book"}</span>
            </div>
            <div className="detail-row">
              <span className="label">Amount</span>
              <span className="value amount">₹{book?.price || "0"}</span>
            </div>
            <div className="detail-row">
              <span className="label">Status</span>
              <span className="value error-status">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path 
                    d="M18 6L6 18" 
                    stroke="#ef4444" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                  <path 
                    d="M6 6L18 18" 
                    stroke="#ef4444" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
                Failed
              </span>
            </div>
            <div className="detail-row">
              <span className="label">Error Code</span>
              <span className="value transaction-id">
                {getErrorCode()}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="failed-actions">
            <button className="btn-primary retry-btn2" onClick={handleRetryPayment}>
              Try Again
            </button>
            <button className="btn-secondary" onClick={handleGoToLibrary}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path 
                  d="M4 19.5A2.5 2.5 0 016.5 17H20" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <path 
                  d="M6.5 2H20V22H6.5A2.5 2.5 0 014 19.5V4.5A2.5 2.5 0 016.5 2Z" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
              Browse Library
            </button>
          </div>

          {/* Help Section */}
          <div className="help-section">
            <div className="help-card">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  strokeWidth="2"
                />
                <path 
                  d="M9.09 9A3 3 0 0112 6C14.83 6 16.08 8.09 16.08 9.5C16.08 11 14.83 12.5 12 12.5" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div>
                <h4>Still having trouble?</h4>
                <p>Our support team is here to help you complete your purchase.</p>
                <button className="support-btn2" onClick={handleContactSupport}>
                  Contact Support
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentFailed;